import {
    BadgeColor,
    BadgeSize,
    ButtonFlavor,
    ButtonType,
    ExBadge,
    ExButton,
    ExEmptyState,
    ExLink,
    ExLoader,
    ExPageHeader,
    LinkSize,
} from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import type { Connection } from '../connections/connections';
import { getConnections } from '../connections/connections';
import ConnectorSidebar from '../connections/ConnectorSidebar';
import ErrorAlertBanner from '../shared/ErrorAlertBanner';
import Table, { type TableColumn } from '../shared/Table';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';
import { FABRIC_BASE_URL } from '../config';
import type { Connector } from './connectors';
import { getConnector } from './connectors';
import styles from './ConnectorDetail.module.css';

interface ConnectorDetailProps {
    connectorId: string;
    onCreateConfig: () => void;
    onEditConfig: (appId: string) => void;
    onBack: (e: CustomEvent) => void;
}

const ConnectorDetail = ({ connectorId, onCreateConfig, onEditConfig }: ConnectorDetailProps) => {
    const [connector, setConnector] = useState<Connector | null>(null);
    const [connections, setConnections] = useState<Connection[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        Promise.all([getConnector(connectorId), getConnections(connectorId)])
            .then(([connectorData, connectionsData]) => {
                setConnector(connectorData);
                setConnections(connectionsData);
            })
            .catch((err) => setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE))
            .finally(() => setLoading(false));
    }, [connectorId]);

    if (loading) return <ExLoader label="Loading…" />;
    if (error) return <ErrorAlertBanner onClose={() => setError(null)}>{error}</ErrorAlertBanner>;
    if (!connector) return null;

    const columns: TableColumn<Connection>[] = [
        {
            header: 'Config Name',
            searchValue: (connection) => connection.name,
            render: (connection) => (
                <ExLink
                    data-pendo-link="connector-configrow-connectionname"
                    data-pendo-connectionid={connection.appId}
                    onClick={() => onEditConfig(connection.appId)}
                    size={LinkSize.SMALL}
                >
                    {connection.name}
                </ExLink>
            ),
        },
        {
            header: 'IDP',
            render: (connection) => connection.identityProviderName,
        },
        {
            header: 'Status',
            render: (connection) => (
                <ExBadge
                    color={connection.enabled ? BadgeColor.GREEN : BadgeColor.GRAY}
                    size={BadgeSize.EXTRASMALL}
                >
                    {connection.enabled ? 'Enabled' : 'Disabled'}
                </ExBadge>
            ),
        },
    ];

    return (
        <div className={styles.layout}>
            <ConnectorSidebar connector={connector} />

            <div className={styles.configsPanel}>
                <ExPageHeader pageTitle="Configs" />
                {connections.length === 0 ? (
                    <ExEmptyState
                        className={styles.empty}
                        label="No configs yet"
                        text="Create a config to get started."
                    >
                        <div slot="header">
                            <img
                                src={`${FABRIC_BASE_URL}/emptyStateIcon.svg`}
                                alt="No configs yet"
                            />
                        </div>
                        <ExButton
                            data-pendo-button="connector-emptystate-createconfig"
                            slot="action"
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            onClick={onCreateConfig}
                        >
                            + Create Config
                        </ExButton>
                    </ExEmptyState>
                ) : (
                    <Table
                        columns={columns}
                        items={connections}
                        rowKey={(connection) => connection.appId}
                        itemLabel="configs"
                        searchPendoTag="connector-configslist-search"
                        actions={
                            <ExButton
                                data-pendo-button="connector-configslist-createconfig"
                                type={ButtonType.PRIMARY}
                                flavor={ButtonFlavor.BRANDED}
                                onClick={onCreateConfig}
                            >
                                + Create Config
                            </ExButton>
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default ConnectorDetail;
